import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  customerChatService,
  CustomerChatMessageItem,
  CustomerBotResponseData,
  CustomerChatQuestionMeta,
} from "../../services/customer-chat.service";

export interface CustomerChatPanelProps {
  open: boolean;
  onClose: () => void;
  order?: any;
}

const ChatPanel: React.FC<CustomerChatPanelProps> = ({ open, onClose, order }) => {
  const [conversationId, setConversationId] = useState<string>("");
  const [messages, setMessages] = useState<CustomerChatMessageItem[]>([]);
  const [inputText, setInputText] = useState<string>("");
  const [options, setOptions] = useState<string[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<CustomerChatQuestionMeta | null>(null);
  const [currentState, setCurrentState] = useState<string>("navigating");
  const [ticketId, setTicketId] = useState<string>("");
  const [isAgentTyping, setIsAgentTyping] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, options, isAgentTyping]);

  // Start or restore chat session when opened
  const initChat = useCallback(async () => {
    if (!open) return;

    setIsLoading(true);
    const orderId = order?.orderId || order?.id || order?._id || order?.orderNumber || "GENERAL-HELP";
    const serviceType = order?.serviceType || "ECOM";

    try {
      const data: CustomerBotResponseData = await customerChatService.startSession(orderId, serviceType);

      setConversationId(data.conversationId);
      setCurrentState(data.currentState || "navigating");
      setOptions(data.options || []);
      setCurrentQuestion(data.question || null);
      if (data.ticketId) setTicketId(data.ticketId);

      const initialMsgs: CustomerChatMessageItem[] = [
        {
          sender: "bot",
          senderName: "Salar Virtual Assistant",
          text: data.message,
          timestamp: new Date().toISOString(),
        },
      ];
      setMessages(initialMsgs);

      // Connect Socket.IO for real-time live support
      customerChatService.connectSocket(data.conversationId, "Customer");
    } catch (err: any) {
      console.error("Failed to start chatbot session:", err);
      // Fallback initial greeting so user can still chat
      const dummyId = `conv-${Date.now()}`;
      setConversationId(dummyId);
      setMessages([
        {
          sender: "bot",
          senderName: "Salar Support",
          text: `👋 Hi! I'm here to help you with Order #${orderId}. How can we assist you today?`,
          timestamp: new Date().toISOString(),
        },
      ]);
      setOptions(["Where is my order? / tracking help", "Cancellation & refund", "Return item", "Talk to Agent 💬"]);
      customerChatService.connectSocket(dummyId, "Customer");
    } finally {
      setIsLoading(false);
    }
  }, [open, order]);

  useEffect(() => {
    if (open) {
      initChat();
    } else {
      customerChatService.disconnectSocket();
      setConversationId("");
      setMessages([]);
      setOptions([]);
      setTicketId("");
      setCurrentQuestion(null);
    }
  }, [open, initChat]);

  // Subscribe to real-time socket events
  useEffect(() => {
    if (!conversationId) return;

    const unsubMsg = customerChatService.onMessage((msg: CustomerChatMessageItem) => {
      setMessages((prev) => {
        if (msg._id && prev.some((m) => m._id === msg._id)) return prev;
        return [...prev, msg];
      });
      if (msg.sender === "agent") {
        setCurrentState("live_support");
      }
    });

    const unsubTyping = customerChatService.onTyping((data) => {
      if (data.sender === "agent") {
        setIsAgentTyping(data.isTyping);
      }
    });

    const unsubEnded = customerChatService.onEnded(() => {
      setCurrentState("end");
      setMessages((prev) => [
        ...prev,
        {
          sender: "system",
          text: "This support conversation has ended. Thank you for shopping with us!",
          timestamp: new Date().toISOString(),
        },
      ]);
    });

    return () => {
      unsubMsg();
      unsubTyping();
      unsubEnded();
    };
  }, [conversationId]);

  // Send structured option click
  const handleSelectOption = async (optionLabel: string) => {
    if (!conversationId) return;

    setMessages((prev) => [
      ...prev,
      {
        sender: "user",
        senderName: "You",
        text: optionLabel,
        timestamp: new Date().toISOString(),
      },
    ]);
    setOptions([]);

    try {
      const matchingOpt = currentQuestion?.options?.find((o) => o.label === optionLabel);

      let data: CustomerBotResponseData;
      if (currentQuestion && matchingOpt) {
        data = await customerChatService.submitAnswer(conversationId, {
          nodeId: currentQuestion.nodeId,
          optionId: matchingOpt.optionId,
        });
      } else {
        data = await customerChatService.sendMessage(conversationId, optionLabel);
      }

      setCurrentState(data.currentState);
      setOptions(data.options || []);
      setCurrentQuestion(data.question || null);
      if (data.ticketId) setTicketId(data.ticketId);

      setMessages((prev) => [
        ...prev,
        {
          sender: data.currentState === "live_support" ? "agent" : "bot",
          senderName: data.currentState === "live_support" ? "Customer Support Agent" : "Salar Virtual Assistant",
          text: data.message,
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      console.error("Error sending answer:", err);
      customerChatService.sendSocketMessage(conversationId, optionLabel, "Customer");
    }
  };

  // Send free-text message
  const handleSendMessage = async () => {
    const text = inputText.trim();
    if (!text || !conversationId) return;

    setInputText("");
    customerChatService.sendTyping(conversationId, false);

    if (currentState === "live_support" || currentState === "ticket_created") {
      setMessages((prev) => [
        ...prev,
        {
          sender: "user",
          senderName: "You",
          text,
          timestamp: new Date().toISOString(),
        },
      ]);
      customerChatService.sendSocketMessage(conversationId, text, "Customer");
      return;
    }

    setMessages((prev) => [
      ...prev,
      {
        sender: "user",
        senderName: "You",
        text,
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const data = await customerChatService.sendMessage(conversationId, text);
      setCurrentState(data.currentState);
      setOptions(data.options || []);
      setCurrentQuestion(data.question || null);
      if (data.ticketId) setTicketId(data.ticketId);

      setMessages((prev) => [
        ...prev,
        {
          sender: data.currentState === "live_support" ? "agent" : "bot",
          senderName: data.currentState === "live_support" ? "Customer Support Agent" : "Salar Virtual Assistant",
          text: data.message,
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      console.error("Error sending message:", err);
      customerChatService.sendSocketMessage(conversationId, text, "Customer");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    if (conversationId) {
      customerChatService.sendTyping(conversationId, e.target.value.length > 0);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !conversationId) return;

    try {
      const res = await customerChatService.uploadImage(conversationId, file);
      setMessages((prev) => [
        ...prev,
        {
          sender: "user",
          senderName: "You",
          text: `[Image: ${file.name}]`,
          fileUrl: res.fileUrl,
          type: "image",
          timestamp: new Date().toISOString(),
        },
        {
          sender: "bot",
          senderName: "Salar Support",
          text: res.message || "Thank you for sharing the photo. Our support team will review it.",
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      console.error("Upload error:", err);
      alert("Failed to upload image. Please try again.");
    }
  };

  if (!open) return null;

  const orderId = order?.orderId || order?.id || order?._id || order?.orderNumber || "Support Chat";
  const orderStatus = order?.status || order?.orderStatus || "";
  const orderAmount = order?.totalAmount || order?.total || order?.items?.[0]?.price || null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 9999,
        animation: "slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      <div
        className="shadow-lg"
        style={{
          width: "380px",
          height: "580px",
          maxHeight: "calc(100vh - 48px)",
          background: "#ffffff",
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid rgba(0, 0, 0, 0.08)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            padding: "12px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
              }}
            >
              <i className="bi bi-robot fs-5"></i>
            </div>
            <div className="lh-sm">
              <div className="fw-bold text-white fs-6 d-flex align-items-center gap-2">
                Salar Help & Support
                {ticketId && (
                  <span className="badge bg-warning text-dark text-xxs px-2 py-0.5 rounded-pill">
                    Ticket #{ticketId.slice(-6)}
                  </span>
                )}
              </div>
              <small className="text-white-50 text-xs">
                {currentState === "live_support"
                  ? "🟢 Connected with Live Agent"
                  : currentState === "ticket_created"
                  ? "🟡 Connecting you with an agent..."
                  : "24/7 Virtual Assistant"}
              </small>
            </div>
          </div>
          <button
            type="button"
            style={{
              background: "transparent",
              border: "none",
              color: "rgba(255, 255, 255, 0.7)",
              fontSize: "16px",
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: "6px",
            }}
            onClick={onClose}
            aria-label="Close Chat"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Order Info Strip */}
        {order && (
          <div
            style={{
              background: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              padding: "8px 16px",
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="text-muted text-xxs text-uppercase fw-bold">Order</span>
                <div className="fw-bold text-dark text-xs">{orderId}</div>
              </div>
              {orderStatus && (
                <span className="badge bg-light text-dark border text-xxs">
                  {orderStatus}
                </span>
              )}
              {orderAmount && (
                <span className="fw-bold text-primary text-xs">
                  ₹{Number(orderAmount).toLocaleString("en-IN")}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Chat Body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px",
            background: "#f1f5f9",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {isLoading && messages.length === 0 ? (
            <div className="text-center py-5 text-muted small">
              <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
              Connecting to support...
            </div>
          ) : (
            messages.map((msg, index) => {
              const isUser = msg.sender === "user";
              const isSystem = msg.sender === "system";
              const isAgent = msg.sender === "agent";

              if (isSystem) {
                return (
                  <div
                    key={index}
                    style={{
                      alignSelf: "center",
                      background: "rgba(0, 0, 0, 0.05)",
                      color: "#64748b",
                      fontSize: "11px",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      margin: "6px 0",
                    }}
                  >
                    <span>{msg.text}</span>
                  </div>
                );
              }

              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                    maxWidth: "85%",
                    alignSelf: isUser ? "flex-end" : "flex-start",
                    flexDirection: isUser ? "row-reverse" : "row",
                  }}
                >
                  {!isUser && (
                    <div
                      style={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        flexShrink: 0,
                        marginTop: "2px",
                        background: isAgent ? "#10b981" : "#e2e8f0",
                        color: isAgent ? "#ffffff" : "#334155",
                      }}
                    >
                      <i className={isAgent ? "bi bi-headset" : "bi bi-robot"}></i>
                    </div>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    {!isUser && (
                      <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", marginLeft: "2px" }}>
                        {isAgent ? msg.senderName || "Support Agent" : "Salar Bot"}
                      </span>
                    )}
                    <div
                      style={{
                        padding: "10px 14px",
                        borderRadius: "14px",
                        fontSize: "13px",
                        lineHeight: 1.45,
                        wordBreak: "break-word",
                        background: isUser
                          ? "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)"
                          : isAgent
                          ? "#ffffff"
                          : "#ffffff",
                        color: isUser ? "#ffffff" : isAgent ? "#065f46" : "#1e293b",
                        border: isUser ? "none" : isAgent ? "1px solid #a7f3d0" : "1px solid #e2e8f0",
                        borderBottomRightRadius: isUser ? "4px" : "14px",
                        borderBottomLeftRadius: !isUser ? "4px" : "14px",
                      }}
                    >
                      {msg.fileUrl ? (
                        <div style={{ maxWidth: "100%", borderRadius: "8px", marginBottom: "6px" }}>
                          <img src={msg.fileUrl} alt="Attachment" style={{ maxWidth: "100%", borderRadius: "6px" }} />
                        </div>
                      ) : null}
                      <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                        {msg.text}
                      </p>
                    </div>
                    {msg.timestamp && (
                      <span style={{ fontSize: "10px", color: "#94a3b8", alignSelf: isUser ? "flex-end" : "flex-start", marginTop: "2px" }}>
                        {new Date(msg.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {/* Agent Typing Indicator */}
          {isAgentTyping && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", alignSelf: "flex-start" }}>
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#10b981",
                  color: "#ffffff",
                  fontSize: "14px",
                }}
              >
                <i className="bi bi-headset"></i>
              </div>
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: "14px",
                  background: "#ffffff",
                  border: "1px solid #a7f3d0",
                  display: "flex",
                  gap: "4px",
                }}
              >
                <span className="spinner-grow spinner-grow-sm text-success" role="status" style={{ width: 6, height: 6 }} />
                <span className="spinner-grow spinner-grow-sm text-success" role="status" style={{ width: 6, height: 6 }} />
                <span className="spinner-grow spinner-grow-sm text-success" role="status" style={{ width: 6, height: 6 }} />
              </div>
            </div>
          )}

          {/* Decision Tree Options */}
          {options.length > 0 && (
            <div
              style={{
                background: "#ffffff",
                border: "1px dashed #cbd5e1",
                borderRadius: "12px",
                padding: "10px 12px",
                marginTop: "4px",
              }}
            >
              <span style={{ fontSize: "11px", fontWeight: "bold", color: "#64748b", paddingLeft: "4px" }}>
                Select an option:
              </span>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {options.map((opt, idx) => (
                  <button
                    key={idx}
                    type="button"
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #cbd5e1",
                      color: "#1e293b",
                      fontSize: "12px",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      cursor: "pointer",
                      fontWeight: 500,
                      transition: "all 0.15s",
                    }}
                    onClick={() => handleSelectOption(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "10px 12px",
            background: "#ffffff",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept="image/*"
            onChange={handleFileUpload}
          />
          <button
            type="button"
            style={{
              background: "transparent",
              border: "none",
              color: "#64748b",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "50%",
            }}
            title="Attach image"
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="bi bi-paperclip fs-5"></i>
          </button>
          <input
            type="text"
            style={{
              flex: 1,
              border: "1px solid #cbd5e1",
              borderRadius: "24px",
              padding: "8px 16px",
              fontSize: "13px",
              outline: "none",
            }}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={
              currentState === "live_support"
                ? "Type message to agent..."
                : "Type your query or reply..."
            }
          />
          <button
            type="button"
            style={{
              background: "transparent",
              border: "none",
              color: inputText.trim() ? "#2563eb" : "#cbd5e1",
              cursor: inputText.trim() ? "pointer" : "not-allowed",
              padding: "6px",
              borderRadius: "50%",
            }}
            onClick={handleSendMessage}
            disabled={!inputText.trim()}
          >
            <i className="bi bi-send-fill fs-5"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;